/**
 * Android "Share to NIVIO" (Web Share Target). The service worker (public/sw.js) receives the
 * POST, keeps the shared text and files in IndexedDB for ten minutes and redirects to
 * /?share=<id>. This module takes the id off the URL at startup, remembers it for the signed-in
 * app, and reads the stash once. The constants must match public/sw.js.
 */
const DB_NAME = 'nivio-share';
const STORE = 'shares';
const TTL_MS = 10 * 60 * 1000;
const PENDING_KEY = 'nivio-chat:share-pending';

export type SharedContent = {
  title: string;
  text: string;
  url: string;
  files: File[];
};

export type ShareStatus = 'unsupported' | 'toolarge' | 'error';
const STATUSES: ReadonlySet<string> = new Set<ShareStatus>(['unsupported', 'toolarge', 'error']);

/** What the redirect left behind: a stash id (hex) or a status word. */
export type PendingShare = { kind: 'id'; id: string } | { kind: 'status'; status: ShareStatus };

let memoryPending: PendingShare | null = null;

function parsePending(value: string | null): PendingShare | null {
  if (!value) return null;
  if (STATUSES.has(value)) return { kind: 'status', status: value as ShareStatus };
  return /^[0-9a-f]{24}$/.test(value) ? { kind: 'id', id: value } : null;
}

/**
 * Call once at startup, before the app renders: removes `?share=` from the address bar (it must
 * not stay in history or be shared on) and remembers it for after sign-in.
 */
export function captureShareParam(): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  const raw = params.get('share');
  if (raw === null) return;
  params.delete('share');
  const query = params.toString();
  try {
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
  } catch {
    // Keep going: the address bar is cosmetic.
  }
  const pending = parsePending(raw);
  if (!pending) return;
  memoryPending = pending;
  try {
    window.sessionStorage.setItem(PENDING_KEY, raw);
  } catch {
    // Storage blocked: the in-memory copy covers a login without a page reload.
  }
}

/** The remembered share, once; cleared as it is returned. */
export function takePendingShare(): PendingShare | null {
  let pending = memoryPending;
  memoryPending = null;
  try {
    pending ??= parsePending(window.sessionStorage.getItem(PENDING_KEY));
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Storage blocked.
  }
  return pending;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no indexedDB'));
      return;
    }
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => open.result.createObjectStore(STORE, { keyPath: 'id' });
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
}

function run<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = action(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(request.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

type Stored = {
  id: string;
  createdAt: number;
  title?: unknown;
  text?: unknown;
  url?: unknown;
  files?: unknown;
};

/**
 * Takes the stash out of IndexedDB: it is deleted whether or not it is still valid. Returns
 * null for an unknown, expired or unreadable entry.
 */
export async function consumeShare(id: string, now = Date.now()): Promise<SharedContent | null> {
  let db: IDBDatabase | null = null;
  try {
    db = await openDb();
    const entry = (await run(db, 'readonly', (store) => store.get(id))) as Stored | undefined;
    await run(db, 'readwrite', (store) => store.delete(id));
    if (!entry || typeof entry.createdAt !== 'number' || now - entry.createdAt > TTL_MS) {
      return null;
    }
    const str = (value: unknown) => (typeof value === 'string' ? value : '');
    const files = Array.isArray(entry.files)
      ? entry.files.filter(
          (file): file is File =>
            typeof file === 'object' &&
            file !== null &&
            typeof (file as File).name === 'string' &&
            typeof (file as File).size === 'number',
        )
      : [];
    return { title: str(entry.title), text: str(entry.text), url: str(entry.url), files };
  } catch {
    return null;
  } finally {
    db?.close();
  }
}

/** The draft text for a share: title, text and link, each once, one per line. */
export function sharedDraftText(content: Pick<SharedContent, 'title' | 'text' | 'url'>): string {
  const parts: string[] = [];
  for (const raw of [content.title, content.text, content.url]) {
    const part = raw.trim();
    // Many apps repeat the link inside the text; do not show it twice.
    if (part && !parts.some((existing) => existing.includes(part))) parts.push(part);
  }
  return parts.join('\n');
}
